import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Activity, Cpu, Database, Clock,
  AlertTriangle, CheckCircle2, XCircle, Server, ListChecks,
  Copy, Check, ChevronRight, ChevronDown, RotateCcw, Zap, Gauge, Timer,
  Users, Wifi, WifiOff, BarChart3, MonitorSmartphone, Bot
} from 'lucide-react';
import AIInsights from './AIInsights';
import { useDesign } from '@/contexts/DesignContext';
import StudioToolHeader from '@/components/studio/admin/StudioToolHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  healthy:  { label: 'Healthy',  Icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30', dot: 'bg-emerald-500' },
  degraded: { label: 'Degraded', Icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30',     dot: 'bg-amber-500' },
  critical: { label: 'Critical', Icon: XCircle,       cls: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',           dot: 'bg-red-500' },
};

/* Studio (§31): the thirteen event types keep their icon and label but take one
   of the state vocabulary's four chips instead of thirteen hues. Mock frame 26. */
const ST_EVENT_TONE = {
  job_error: 'st-tb-bad', webhook_error: 'st-tb-bad', traffic_drop: 'st-tb-bad', error_rate_spike: 'st-tb-bad',
  loop_lag: 'st-tb-warn', mem_pressure: 'st-tb-warn', traffic_brownout: 'st-tb-warn', slow_request: 'st-tb-warn', slow_job: 'st-tb-warn',
  traffic_resume: 'st-tb-ok', traffic_brownout_resume: 'st-tb-ok',
  boot: '', deploy: '',
};

const fmtUptime = (s) => {
  if (s == null) return '—';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ');
};
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

// Flatten one captured error into copy-paste-friendly plain text so a super-admin
// can paste it straight back to the developer to diagnose.
const errToText = (e) => [
  `[${e.status}] ${e.method} ${e.path}${e.query ? `?${e.query}` : ''}`,
  `time: ${e.ts}`,
  e.client ? `client: ${e.client}` : null,
  e.detail ? `detail: ${e.detail}` : null,
  e.traceback ? `traceback:\n${e.traceback}` : null,
].filter(Boolean).join('\n');

const fmtDuration = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), sec = s % 60;
  return sec ? `${m}m ${sec}s` : `${m}m`;
};

// Discrete-event styling for the brownout timeline.
const EVENT_META = {
  boot:         { label: 'Restart',         Icon: RotateCcw,  cls: 'text-sky-600 dark:text-sky-400',        bg: 'bg-sky-100 dark:bg-sky-500/20' },
  deploy:       { label: 'Deploy',          Icon: RotateCcw,  cls: 'text-indigo-600 dark:text-indigo-400',  bg: 'bg-indigo-100 dark:bg-indigo-500/20' },
  loop_lag:     { label: 'Event-loop lag',  Icon: Zap,        cls: 'text-amber-600 dark:text-amber-400',    bg: 'bg-amber-100 dark:bg-amber-500/20' },
  mem_pressure: { label: 'Memory pressure', Icon: Cpu,        cls: 'text-orange-600 dark:text-orange-400',  bg: 'bg-orange-100 dark:bg-orange-500/20' },
  slow_request: { label: 'Slow request',    Icon: Timer,      cls: 'text-violet-600 dark:text-violet-400',  bg: 'bg-violet-100 dark:bg-violet-500/20' },
  slow_job:     { label: 'Slow job',        Icon: ListChecks, cls: 'text-fuchsia-600 dark:text-fuchsia-400',bg: 'bg-fuchsia-100 dark:bg-fuchsia-500/20' },
  job_error:    { label: 'Job error',       Icon: XCircle,    cls: 'text-red-600 dark:text-red-400',        bg: 'bg-red-100 dark:bg-red-500/20' },
  webhook_error: { label: 'Webhook error',  Icon: XCircle,    cls: 'text-red-600 dark:text-red-400',        bg: 'bg-red-100 dark:bg-red-500/20' },
  traffic_drop:   { label: 'Traffic → 0',     Icon: WifiOff, cls: 'text-red-600 dark:text-red-400',         bg: 'bg-red-100 dark:bg-red-500/20' },
  traffic_resume: { label: 'Traffic resumed', Icon: Wifi,    cls: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
  traffic_brownout:        { label: 'Brownout',       Icon: WifiOff, cls: 'text-amber-600 dark:text-amber-400',     bg: 'bg-amber-100 dark:bg-amber-500/20' },
  traffic_brownout_resume: { label: 'Brownout ended', Icon: Wifi,    cls: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
  error_rate_spike:        { label: '5xx spike',      Icon: XCircle, cls: 'text-red-600 dark:text-red-400',         bg: 'bg-red-100 dark:bg-red-500/20' },
};

// Flatten an event into copy-paste-friendly text for pasting back to the dev.
const evtToText = (e) => {
  const extra = Object.entries(e)
    .filter(([k]) => !['ts', 'type', 'message'].includes(k))
    .map(([k, v]) => `${k}=${v}`).join(' ');
  return `[${(EVENT_META[e.type]?.label || e.type)}] ${e.ts}  ${e.message || ''}${extra ? `  (${extra})` : ''}`;
};

// What the client saw — one line per report, paste-back-friendly.
const clientErrToText = (e) =>
  `[client saw ${e.status ? e.status : e.kind}] ${e.ts}  ${e.method} ${e.path}` +
  `${e.message ? ` · ${e.message}` : ''} · page=${e.page || '—'} app=${e.app}` +
  ` online=${e.online} client=${e.client || '—'} uploaded=${e.received_at}`;

// Human label for a client failure: exact status if the edge answered, else the
// network-level failure class (connection refused / TLS / DNS look identical
// to the browser: a TypeError with status 0).
const clientStatusLabel = (e) => (e.status ? String(e.status) : 'NETWORK');

// Tiny dependency-free sparkline (SVG). values: number[]; higher = taller.
const Sparkline = ({ values, color = '#6366f1', height = 36, warnAt, studio }) => {
  const v = (values || []).filter((x) => x != null);
  if (v.length < 2) return <div className={studio ? 'st31-col' : 'text-[11px] text-slate-400 py-3'}>collecting…</div>;
  const max = Math.max(1, ...v), min = Math.min(0, ...v);
  const span = max - min || 1;
  const W = 100, pts = v.map((x, i) => `${(i / (v.length - 1)) * W},${height - ((x - min) / span) * height}`).join(' ');
  const breached = warnAt != null && max >= warnAt;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" className={studio ? 'st31-sl' : 'w-full'} style={studio ? undefined : { height }}>
      <polyline points={pts} fill="none" stroke={studio ? (breached ? 'var(--st-warn)' : 'var(--st-acc)') : (breached ? '#f59e0b' : color)} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const SparkCard = ({ Icon, label, values, latest, unit, color, warnAt, testid }) => {
  const { isStudio } = useDesign();
  if (isStudio) {
    return (
      <div data-testid={testid}>
        <div className="st31-l">
          <span><Icon /> {label}</span>
          <b>{latest != null ? `${latest}${unit || ''}` : '—'}</b>
        </div>
        <Sparkline values={values} warnAt={warnAt} studio />
      </div>
    );
  }
  return (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-4">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-sm font-bold text-slate-900 dark:text-white">{latest != null ? `${latest}${unit || ''}` : '—'}</div>
    </div>
    <Sparkline values={values} color={color} warnAt={warnAt} />
  </div>
  );
};

// Hourly traffic bar chart (dependency-free). data oldest→newest.
const HourlyBars = ({ data }) => {
  const { isStudio } = useDesign();
  if (!data || data.length === 0) {
    return isStudio
      ? <div className="st31-quiet" data-testid="hourly-traffic-empty"><b>Collecting.</b><span>Hourly data appears after the first full clock hour.</span></div>
      : <div className="text-[11px] text-slate-400 py-6 text-center" data-testid="hourly-traffic-empty">collecting… hourly data appears after the first full clock hour.</div>;
  }
  const max = Math.max(1, ...data.map((d) => d.req_count || 0));
  if (isStudio) {
    return (
      <div className="st31-hbars" data-testid="hourly-traffic-bars">
        {data.map((d, i) => {
          const pct = Math.max(2, Math.round(((d.req_count || 0) / max) * 100));
          const label = d.hour_start ? new Date(d.hour_start) : null;
          const title = `${label ? label.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' }) : `hour ${i}`} — ${d.req_count || 0} reqs · ${d.distinct_active_users || 0} active users · ${d.peak_online_users || 0} peak online`;
          return <div key={d.hour_start || i} title={title} data-testid={`hourly-bar-${i}`} style={{ height: `${pct}%` }} />;
        })}
      </div>
    );
  }
  return (
    <div className="flex items-end gap-px h-32 w-full" data-testid="hourly-traffic-bars">
      {data.map((d, i) => {
        const pct = Math.max(2, Math.round(((d.req_count || 0) / max) * 100));
        const label = d.hour_start ? new Date(d.hour_start) : null;
        const title = `${label ? label.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' }) : `hour ${i}`} — ${d.req_count || 0} reqs · ${d.distinct_active_users || 0} active users · ${d.peak_online_users || 0} peak online`;
        return (
          <div key={d.hour_start || i} title={title} data-testid={`hourly-bar-${i}`}
            className="flex-1 min-w-0 flex flex-col justify-end h-full">
            <div className="bg-emerald-500/80 hover:bg-emerald-400 rounded-t-sm transition-colors" style={{ height: `${pct}%` }} />
          </div>
        );
      })}
    </div>
  );
};

const Metric = ({ Icon, label, value, sub, testid, tone = '' }) => {
  const { isStudio } = useDesign();
  if (isStudio) {
    const t = tone.includes('red') ? 'st31-bad' : tone.includes('amber') ? 'st31-warn' : '';
    return (
      <div data-testid={testid} className={`st31-htile${t === 'st31-bad' ? ' st31-tbad' : ''}`}>
        <div className="st31-l"><Icon /> {label}</div>
        <div className={`st31-v ${t}`}>{value}</div>
        {sub && <div className="st31-s">{sub}</div>}
      </div>
    );
  }
  return (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5">
    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className={`mt-2 text-2xl font-bold ${tone || 'text-slate-900 dark:text-white'}`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
  </div>
  );
};

export default function HealthDashboard({ user, onClose }) {
  const { isStudio } = useDesign();
  const [snap, setSnap] = useState(null);
  const [errors, setErrors] = useState([]);
  const [daily, setDaily] = useState([]);
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [clientErrs, setClientErrs] = useState([]);
  const [clientSummary, setClientSummary] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [hourlyView, setHourlyView] = useState(48);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [expandedErr, setExpandedErr] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const email = user?.email;
  const inFlight = useRef(false);

  const copy = useCallback((text, id) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    }).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    if (!email || inFlight.current) return;
    inFlight.current = true;
    try {
      const q = `user_email=${encodeURIComponent(email)}`;
      const [s, e, d, ev, m, h, ce] = await Promise.all([
        fetch(`${API_URL}/api/admin/health?${q}`),
        fetch(`${API_URL}/api/admin/health/errors?${q}&limit=100`),
        fetch(`${API_URL}/api/admin/health/daily?${q}&days=30`),
        fetch(`${API_URL}/api/admin/health/events?${q}&limit=100`),
        fetch(`${API_URL}/api/admin/health/metrics?${q}&minutes=120`),
        fetch(`${API_URL}/api/admin/health/hourly?${q}&hours=168`),
        fetch(`${API_URL}/api/admin/health/client-errors?${q}&limit=200`),
      ]);
      if (!s.ok) throw new Error(`Health check failed (${s.status})`);
      setSnap(await s.json());
      setErrors(e.ok ? (await e.json()).errors || [] : []);
      setDaily(d.ok ? (await d.json()).days || [] : []);
      setEvents(ev.ok ? (await ev.json()).events || [] : []);
      setMetrics(m.ok ? (await m.json()).metrics || [] : []);
      setHourly(h.ok ? (await h.json()).hourly || [] : []);
      if (ce.ok) {
        const cj = await ce.json();
        setClientErrs(cj.errors || []);
        setClientSummary(cj.summary_24h || []);
      } else { setClientErrs([]); setClientSummary([]); }
      setUpdatedAt(new Date());
      setLoadErr('');
    } catch (err) {
      setLoadErr(err.message || 'Failed to load health');
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [email]);

  // Initial load + light auto-refresh: only every 60s AND only while the tab is
  // visible (pauses when hidden) — keeps server load minimal.
  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchAll();
    }, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchAll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchAll]);

  const st = STATUS[snap?.status] || STATUS.healthy;
  const StatusIcon = st.Icon;
  const mongo = snap?.mongo || {};
  const proc = snap?.process || {};
  const res = snap?.resources || {};
  const lag = snap?.event_loop?.lag_ms;
  const stab = snap?.stability || {};
  const hadDowntime = (stab.last_downtime_gap_s || 0) > 0;
  // Series for sparklines (oldest→newest).
  const sLag = metrics.map((x) => x.lag_ms);
  const sMem = metrics.map((x) => x.mem_pct);
  const sReq = metrics.map((x) => x.req_count);
  const sErr = metrics.map((x) => x.err_count);
  const sConc = metrics.map((x) => x.inflight_peak);
  const sFd = metrics.map((x) => x.fd_count);
  const last = metrics[metrics.length - 1] || {};
  // Hourly traffic/demand (oldest→newest); slice to the selected window.
  const shownHourly = hourly.slice(-hourlyView);
  const trafficTotalReqs = shownHourly.reduce((a, x) => a + (x.req_count || 0), 0);
  const trafficPeakUsers = shownHourly.reduce((a, x) => Math.max(a, x.distinct_active_users || 0), 0);
  const trafficPeakOnline = shownHourly.reduce((a, x) => Math.max(a, x.peak_online_users || 0), 0);
  const firstHourLabel = shownHourly[0]?.hour_start
    ? new Date(shownHourly[0].hour_start).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' })
    : '';

  const stTone = snap?.status === 'critical' ? 'st-tb-bad' : snap?.status === 'degraded' ? 'st-tb-warn' : 'st-tb-ok';

  return (
    <div className={isStudio ? 'st31-page st31-hshell' : 'min-h-screen bg-slate-50 dark:bg-slate-950'} data-testid="health-dashboard">
      {isStudio && (
        /* Studio (§31): the app tokens instead of classic's slate-950 ramp, and
           the same header every other tool in this area wears — "Updated" plus a
           secondary Refresh (mock questions 3 and 15). Frames 25, 26, 30. */
        <StudioToolHeader
          icon={Activity}
          title="System health"
          sub={`${updatedAt ? `Last checked ${updatedAt.toLocaleTimeString()}` : 'Checking…'} · refreshes every 60 seconds while open`}
          onBack={onClose}
          backTestid="health-back-btn"
          right={<>
            {updatedAt && (
              <span className="st31-cap st31-num">
                Updated {updatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </span>
            )}
            <button type="button" className="st-btn st-sec st-sm" onClick={fetchAll} disabled={loading} data-testid="health-refresh-btn">
              <RefreshCw className={loading ? 'st31-spinning' : undefined} /> <span className="st31-btnlbl">{loading ? 'Refreshing…' : 'Refresh'}</span>
            </button>
          </>}
        />
      )}
      <div className={isStudio ? 'st31-pg st31-stack' : 'max-w-5xl mx-auto px-4 sm:px-6 py-6'}>
        {/* Header */}
        {!isStudio && (<>
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} data-testid="health-back-btn"
            className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={fetchAll} data-testid="health-refresh-btn"
            className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90 disabled:opacity-50"
            disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="flex items-center gap-3 mb-1">
          <Activity className="w-6 h-6 text-slate-900 dark:text-white" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">System Health</h1>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {updatedAt ? `Last checked ${updatedAt.toLocaleTimeString()}` : 'Loading…'} · auto-refreshes every 60s while open
        </p>
        </>)}

        {loadErr && (
          <div data-testid="health-load-error" className={isStudio ? 'st-notice st-notice-bad' : 'mb-6 rounded-2xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300'}>
            {isStudio && <AlertTriangle />}
            <div>{loadErr}{isStudio && <> <button type="button" className="st-lnk" onClick={fetchAll}>Retry</button></>}</div>
          </div>
        )}

        {/* Overall status */}
        {isStudio ? (
          <div data-testid="health-status-banner" className="st31-status">
            <span className={`st-chip ${stTone}`}><StatusIcon />{st.label}</span>
            <span className="st31-r">{snap?.reasons?.length ? snap.reasons.join(' · ') : 'All systems nominal.'}</span>
          </div>
        ) : (
        <div data-testid="health-status-banner" className={`rounded-2xl border p-5 mb-6 ${st.bg}`}>
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-7 h-7 ${st.cls}`} />
            <div>
              <div className={`text-lg font-bold ${st.cls}`}>{st.label}</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {snap?.reasons?.length ? snap.reasons.join(' · ') : 'All systems nominal.'}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Metrics grid */}
        {isStudio && <div className="st31-card"><div className="st31-ch"><h2 className="st31-ct">Runtime</h2></div><div className="st31-cb"><div className="st31-g4">
          <Metric testid="health-loop-lag" Icon={Clock} label="Event-loop lag"
            value={lag != null ? `${lag} ms` : '—'}
            tone={lag > 1000 ? 'text-red-600 dark:text-red-400' : lag > 250 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub="Brownout early warning. Under 250 ms is healthy." />
          <Metric testid="health-memory" Icon={Cpu} label="Memory (RSS)"
            value={proc.rss_mb != null ? `${proc.rss_mb} MB` : '—'}
            tone={proc.mem_pct > 92 ? 'text-red-600 dark:text-red-400' : proc.mem_pct > 80 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub={proc.mem_limit_mb ? `${proc.mem_pct ?? '—'}% of the ${proc.mem_limit_mb} MB limit` : 'limit unknown'} />
          <Metric testid="health-mongo" Icon={Database} label="MongoDB"
            value={mongo.ok ? `${mongo.ping_ms} ms` : 'Down'}
            tone={!mongo.ok ? 'text-red-600 dark:text-red-400' : mongo.ping_ms > 500 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub={mongo.connections ? `${mongo.connections.current} connections, ${mongo.connections.active} active` : 'ping latency'} />
          <Metric testid="health-uptime" Icon={Server} label="Uptime"
            value={fmtUptime(proc.uptime_seconds)} sub={`pod ${proc.hostname || '—'} · pid ${proc.pid ?? '—'}`} />
        </div></div></div>}
        {!isStudio && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Metric testid="health-loop-lag" Icon={Clock} label="Event-Loop Lag"
            value={lag != null ? `${lag} ms` : '—'}
            tone={lag > 1000 ? 'text-red-600 dark:text-red-400' : lag > 250 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub="Brownout early-warning (<250ms healthy)" />
          <Metric testid="health-memory" Icon={Cpu} label="Memory (RSS)"
            value={proc.rss_mb != null ? `${proc.rss_mb} MB` : '—'}
            tone={proc.mem_pct > 92 ? 'text-red-600 dark:text-red-400' : proc.mem_pct > 80 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub={proc.mem_limit_mb ? `${proc.mem_pct ?? '—'}% of ${proc.mem_limit_mb} MB limit` : 'limit unknown'} />
          <Metric testid="health-mongo" Icon={Database} label="MongoDB"
            value={mongo.ok ? `${mongo.ping_ms} ms` : 'DOWN'}
            tone={!mongo.ok ? 'text-red-600 dark:text-red-400' : mongo.ping_ms > 500 ? 'text-amber-600 dark:text-amber-400' : ''}
            sub={mongo.connections ? `${mongo.connections.current} conns (${mongo.connections.active} active)` : 'ping latency'} />
          <Metric testid="health-uptime" Icon={Server} label="Uptime"
            value={fmtUptime(proc.uptime_seconds)} sub={`pod ${proc.hostname || '—'} · pid ${proc.pid ?? '—'}`} />
        </div>
        )}

        {/* Stability / brownout history */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-stability-card">
          {isStudio
            ? <div className="st31-ch"><h2 className="st31-ct">Stability and brownout history</h2></div>
            : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white mb-4">
                <RotateCcw className="w-4 h-4 text-sky-500" /> Stability & Brownout History
              </div>}
          <div className={isStudio ? 'st31-cb' : undefined}>
          <div className={isStudio ? 'st31-g4' : 'grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4'}>
            <div data-testid="stability-restarts" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'text-[11px] uppercase tracking-wider text-slate-400'}>{isStudio && <RotateCcw />}Restarts (24h)</div>
              <div className={isStudio ? `st31-v ${stab.restarts_24h > 3 ? 'st31-warn' : ''}` : `text-xl font-bold ${stab.restarts_24h > 3 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>{stab.restarts_24h ?? '—'}</div>
            </div>
            <div data-testid="stability-downtime" className={isStudio ? `st31-htile${hadDowntime ? ' st31-tbad' : ''}` : `rounded-xl border p-3 ${hadDowntime ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10' : 'border-slate-200 dark:border-white/10'}`}>
              <div className={isStudio ? 'st31-l' : 'text-[11px] uppercase tracking-wider text-slate-400'}>{isStudio && <WifiOff />}{isStudio ? 'Last downtime gap' : 'Last Downtime Gap'}</div>
              <div className={isStudio ? `st31-v ${hadDowntime ? 'st31-bad' : ''}` : `text-xl font-bold ${hadDowntime ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>{fmtDuration(stab.last_downtime_gap_s)}</div>
            </div>
            <div data-testid="stability-looplag-events" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'text-[11px] uppercase tracking-wider text-slate-400'}>{isStudio && <Zap />}{isStudio ? 'Loop-lag events (24h)' : 'Loop-Lag Events (24h)'}</div>
              <div className={isStudio ? `st31-v ${stab.loop_lag_events_24h > 0 ? 'st31-warn' : ''}` : `text-xl font-bold ${stab.loop_lag_events_24h > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>{stab.loop_lag_events_24h ?? '—'}</div>
            </div>
            <div data-testid="stability-lastboot" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'text-[11px] uppercase tracking-wider text-slate-400'}>{isStudio && <Clock />}{isStudio ? 'Last restart' : 'Last Restart'}</div>
              <div className={isStudio ? 'st31-v' : 'text-sm font-semibold text-slate-900 dark:text-white mt-1'} style={isStudio ? { fontSize: 18 } : undefined}>{stab.last_boot_ts ? new Date(stab.last_boot_ts).toLocaleString() : '—'}</div>
            </div>
          </div>
          {hadDowntime && (
            <div className={isStudio ? 'st-notice st-notice-bad' : 'text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-2.5'} style={isStudio ? { marginTop: 12 } : undefined}>
              {isStudio ? (<><AlertTriangle /><div>The app was unreachable for {fmtDuration(stab.last_downtime_gap_s)} before the last restart. An external monitor would have seen that window as 502 or 503.</div></>)
                : <>⚠️ The app was unreachable for {fmtDuration(stab.last_downtime_gap_s)} before the last restart — a brownout window external monitors would have seen as 503/502.</>}
            </div>
          )}
          </div>
        </div>

        {/* Live telemetry sparklines (last 2h) */}
        {(() => {
          const sparks = (<>
            <SparkCard testid="spark-lag" Icon={Zap} label={isStudio ? 'Loop lag' : 'Loop Lag'} values={sLag} latest={last.lag_ms} unit="ms" color="#6366f1" warnAt={250} />
            <SparkCard testid="spark-mem" Icon={Cpu} label="Memory" values={sMem} latest={last.mem_pct} unit="%" color="#0ea5e9" warnAt={80} />
            <SparkCard testid="spark-req" Icon={Activity} label="Req / min" values={sReq} latest={last.req_count} color="#10b981" />
            <SparkCard testid="spark-conc" Icon={Gauge} label={isStudio ? 'Peak conc.' : 'Peak Conc.'} values={sConc} latest={last.inflight_peak} color="#f59e0b" warnAt={150} />
            <SparkCard testid="spark-err" Icon={AlertTriangle} label="5xx / min" values={sErr} latest={last.err_count} color="#ef4444" warnAt={1} />
          </>);
          return isStudio ? (
            <div className="st31-card">
              <div className="st31-ch"><h2 className="st31-ct">Telemetry<small>last 120 minutes</small></h2></div>
              <div className="st31-cb">
                <div className="st31-spark" data-testid="health-telemetry-grid">{sparks}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="health-telemetry-grid">{sparks}</div>
          );
        })()}

        {/* Resources since boot: the non-memory counters that creep over a long
            uptime (FDs/sockets, threads, asyncio tasks, WS) — the real leak canaries */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-resources-card">
          {isStudio
            ? <div className="st31-ch"><h2 className="st31-ct">Resources<small>leak canaries</small></h2></div>
            : <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1">
                <Server className="w-4 h-4" /> Resources (leak canaries)
              </div>}
          <div className={isStudio ? 'st31-cb' : undefined}>
          <p className={isStudio ? 'st31-cap' : 'text-[11px] text-slate-500 dark:text-slate-400 mb-4'}>RSS looks fine even during these blackouts — watch these instead. A steady climb over uptime (esp. open files / FDs) points to a non-memory resource leak.</p>
          <div className={isStudio ? 'st31-g4' : 'grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4'}>
            <Metric Icon={ListChecks} label="Open files (FDs)" testid="res-fd" value={res.fd_count ?? '—'} sub="sockets + files" />
            <Metric Icon={Cpu} label="Threads" testid="res-threads" value={res.threads ?? '—'} />
            <Metric Icon={Activity} label="Async tasks" testid="res-tasks" value={res.tasks ?? '—'} />
            <Metric Icon={Wifi} label="WS sockets" testid="res-ws" value={res.ws_sockets ?? '—'} sub={`${res.ws_conns ?? 0} users`} />
          </div>
          {isStudio
            ? <div className="st31-spark" style={{ gridTemplateColumns: '1fr' }}><SparkCard testid="spark-fd" Icon={ListChecks} label="Open files (FDs) / min" values={sFd} latest={last.fd_count} warnAt={2000} /></div>
            : <SparkCard testid="spark-fd" Icon={ListChecks} label="Open files (FDs) / min" values={sFd} latest={last.fd_count} color="#a855f7" warnAt={2000} />}
          </div>
        </div>

        {/* Traffic & Demand (hourly): peaks/lulls + active/online users */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-traffic-card">
          <div className={isStudio ? 'st31-ch' : 'flex items-center justify-between mb-4'}>
            {isStudio
              ? <h2 className="st31-ct">Traffic and demand</h2>
              : <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 text-sm font-semibold">
                  <BarChart3 className="w-4 h-4" /> Traffic &amp; Demand
                </div>}
            <div className={isStudio ? 'st-segc st31-cr' : 'flex gap-1'} data-testid="traffic-range-toggle">
              {[['24h', 24], ['48h', 48], ['7d', 168]].map(([lbl, n]) => (
                <button key={n} onClick={() => setHourlyView(n)} data-testid={`traffic-range-${lbl}`}
                  className={isStudio
                    ? (hourlyView === n ? 'st-on' : undefined)
                    : `px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${hourlyView === n ? 'bg-emerald-600 text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10'}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div className={isStudio ? 'st31-cb' : undefined}>
          <div className={isStudio ? 'st31-g3' : 'grid grid-cols-3 gap-3 mb-4'}>
            <div data-testid="traffic-total-reqs" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-[11px] font-semibold uppercase tracking-wider'}><Activity className={isStudio ? undefined : 'w-3.5 h-3.5'} /> Requests</div>
              <div className={isStudio ? 'st31-v' : 'mt-1 text-xl font-bold text-slate-900 dark:text-white'}>{trafficTotalReqs.toLocaleString()}</div>
              <div className={isStudio ? 'st31-s' : 'text-[11px] text-slate-500 dark:text-slate-400'}>{isStudio ? 'in the selected window' : 'in selected window'}</div>
            </div>
            <div data-testid="traffic-peak-users" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-[11px] font-semibold uppercase tracking-wider'}><Users className={isStudio ? undefined : 'w-3.5 h-3.5'} /> Peak active users</div>
              <div className={isStudio ? 'st31-v' : 'mt-1 text-xl font-bold text-slate-900 dark:text-white'}>{trafficPeakUsers.toLocaleString()}</div>
              <div className={isStudio ? 'st31-s' : 'text-[11px] text-slate-500 dark:text-slate-400'}>busiest single hour</div>
            </div>
            <div data-testid="traffic-peak-online" className={isStudio ? 'st31-htile' : 'rounded-xl border border-slate-200 dark:border-white/10 p-3'}>
              <div className={isStudio ? 'st31-l' : 'flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-[11px] font-semibold uppercase tracking-wider'}><Wifi className={isStudio ? undefined : 'w-3.5 h-3.5'} /> Peak online</div>
              <div className={isStudio ? 'st31-v' : 'mt-1 text-xl font-bold text-slate-900 dark:text-white'}>{trafficPeakOnline.toLocaleString()}</div>
              <div className={isStudio ? 'st31-s' : 'text-[11px] text-slate-500 dark:text-slate-400'}>{isStudio ? 'most at once' : 'max concurrent'}</div>
            </div>
          </div>
          <HourlyBars data={shownHourly} />
          <div className={isStudio ? undefined : 'flex justify-between text-[10px] text-slate-400 mt-1'} style={isStudio ? { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--st-fg3)' } : undefined}>
            <span>{firstHourLabel}</span><span>now</span>
          </div>
          <div className={isStudio ? 'st31-cap' : 'mt-2 text-[11px] text-slate-500 dark:text-slate-400'}>{isStudio
            ? "Bars are requests per hour. Hover one for that hour's requests, distinct active users and peak simultaneous online users."
            : "Bars = requests per hour. Hover a bar for that hour's requests, distinct active users, and peak simultaneous online users."}</div>
          </div>
        </div>

        {/* Brownout event timeline */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-events-card">
          <div className={isStudio ? 'st31-ch' : 'flex items-center justify-between mb-3'}>
            {isStudio
              ? <h2 className="st31-ct">Event timeline<small>restarts, lag, memory, slow requests and jobs</small></h2>
              : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                  <Activity className="w-4 h-4 text-sky-500" /> Event Timeline
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">(restarts, lag, memory, slow requests & jobs)</span>
                </div>}
            {events.length > 0 && (
              <button onClick={() => copy(events.map(evtToText).join('\n'), 'all-events')}
                data-testid="health-events-copy-all"
                className={isStudio ? 'st-btn st-ghost st-sm st31-cr' : 'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg border border-slate-200 dark:border-white/15 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}>
                {copiedId === 'all-events' ? <><Check className={isStudio ? undefined : 'w-3 h-3'} /> Copied</> : <><Copy className={isStudio ? undefined : 'w-3 h-3'} /> Copy all</>}
              </button>
            )}
          </div>
          <div className={isStudio ? 'st31-cb' : undefined}>
          {events.length === 0 ? (
            isStudio
              ? <div className="st31-quiet"><b>No stability events recorded 🎉</b></div>
              : <p className="text-sm text-slate-500 dark:text-slate-400">No stability events recorded. 🎉</p>
          ) : (
            <div className={isStudio ? 'st31-scroll' : 'max-h-96 overflow-auto divide-y divide-slate-100 dark:divide-white/5'}>
              {events.map((e, i) => {
                const meta = EVENT_META[e.type] || { label: e.type, Icon: Activity, cls: 'text-slate-500', bg: 'bg-slate-100 dark:bg-white/10' };
                const EvIcon = meta.Icon;
                const detail = [
                  e.duration_ms != null && `${e.duration_ms}ms`,
                  e.downtime_gap_s != null && `gap ${fmtDuration(e.downtime_gap_s)}`,
                  e.inflight_peak != null && `peak ${e.inflight_peak} conc`,
                  e.req_count != null && `${e.req_count} req/min`,
                  e.mem_pct != null && `${e.mem_pct}% mem`,
                  e.job_id && `job ${e.job_id}`,
                ].filter(Boolean).join(' · ');
                return (
                  <div key={e.ts || `ev-${i}`} className={isStudio ? 'st31-tl' : 'py-2 flex items-start gap-3 text-sm'} data-testid={`health-event-row-${i}`}>
                    <span className={isStudio ? `st-chip st-tb-xs ${ST_EVENT_TONE[e.type] || ''}` : `mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${meta.bg} ${meta.cls} shrink-0`}>
                      <EvIcon className={isStudio ? undefined : 'w-3 h-3'} /> {meta.label}
                    </span>
                    <div className={isStudio ? 'st31-b' : 'min-w-0 flex-1'}>
                      <div className={isStudio ? 'st31-x' : 'text-xs text-slate-700 dark:text-slate-300 truncate'}>{e.message || '—'}</div>
                      <div className={isStudio ? 'st31-m' : 'text-[11px] text-slate-400'}>{fmtTime(e.ts)}{detail ? ` · ${detail}` : ''}</div>
                    </div>
                    <button onClick={() => copy(evtToText(e), e.ts || `ev-${i}`)} data-testid={`health-event-copy-${i}`}
                      className={isStudio ? 'st-ibtn st31-copy' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shrink-0'}
                      style={isStudio ? { width: 28, height: 28 } : undefined} aria-label="Copy this event">
                      {copiedId === (e.ts || `ev-${i}`) ? <Check className={isStudio ? undefined : 'w-3.5 h-3.5'} /> : <Copy className={isStudio ? undefined : 'w-3.5 h-3.5'} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* Errors */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-errors-card">
          <div className={isStudio ? 'st31-ch' : 'flex items-center justify-between mb-3'}>
            {isStudio
              ? <h2 className="st31-ct">Recent errors (5xx)<small>{snap?.errors?.last_1h ?? 0} in the last hour · {snap?.errors?.last_24h ?? 0} in 24h</small></h2>
              : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Recent Errors (5xx)
                </div>}
            <div className={isStudio ? 'st31-cr' : 'flex items-center gap-3'}>
              {!isStudio && <div className="text-xs text-slate-500 dark:text-slate-400">
                {snap?.errors?.last_1h ?? 0} in last hour · {snap?.errors?.last_24h ?? 0} in 24h
              </div>}
              {errors.length > 0 && (
                <button onClick={() => copy(errors.map(errToText).join('\n\n---\n\n'), 'all')}
                  data-testid="health-error-copy-all"
                  className={isStudio ? 'st-btn st-ghost st-sm' : 'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg border border-slate-200 dark:border-white/15 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}>
                  {copiedId === 'all' ? <><Check className={isStudio ? undefined : 'w-3 h-3'} /> Copied</> : <><Copy className={isStudio ? undefined : 'w-3 h-3'} /> Copy all</>}
                </button>
              )}
            </div>
          </div>
          <div className={isStudio ? 'st31-cb' : undefined}>
          {errors.length === 0 ? (
            isStudio
              ? <div className="st31-quiet"><b>No errors recorded 🎉</b></div>
              : <p className="text-sm text-slate-500 dark:text-slate-400">No errors recorded. 🎉</p>
          ) : (
            <div className={isStudio ? 'st31-scroll' : 'max-h-96 overflow-auto divide-y divide-slate-100 dark:divide-white/5'}>
              {errors.map((e, i) => {
                const rowKey = e.ts || `idx-${i}`;  // stable across refresh/reorder (index is not)
                const isOpen = expandedErr === rowKey;
                return (
                  <div key={rowKey} className={isStudio ? undefined : 'py-2 text-sm'} style={isStudio ? { padding: '9px 0', borderTop: '1px solid var(--st-line)' } : undefined} data-testid={`health-error-row-${i}`}>
                    <button onClick={() => setExpandedErr(isOpen ? null : rowKey)}
                      data-testid={`health-error-toggle-${i}`}
                      className="w-full flex items-start gap-3 text-left">
                      {isOpen
                        ? <ChevronDown className="w-3.5 h-3.5 mt-1 text-slate-400 shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 mt-1 text-slate-400 shrink-0" />}
                      <span className={isStudio ? 'st-chip st-tb-xs st-tb-bad st31-mono' : 'font-mono text-xs px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 shrink-0'}>{e.status}</span>
                      <div className="min-w-0 flex-1">
                        <div className={isStudio ? 'st31-mono st31-x' : 'font-mono text-xs text-slate-700 dark:text-slate-300 truncate'}>{e.method} {e.path}</div>
                        <div className={isStudio ? 'st31-m' : 'text-[11px] text-slate-400'}>{fmtTime(e.ts)}{e.detail ? ` · ${e.detail}` : ''}</div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className={isStudio ? 'st31-detail' : 'mt-2 ml-7 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-white/10 p-3 space-y-2'} data-testid={`health-error-detail-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={isStudio ? 'st31-lbl' : 'text-[11px] font-semibold uppercase tracking-wider text-slate-400'}>Details</span>
                          <button onClick={() => copy(errToText(e), rowKey)}
                            data-testid={`health-error-copy-${i}`}
                            className={isStudio ? 'st-btn st-ghost st-sm' : 'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:opacity-90'}>
                            {copiedId === rowKey ? <><Check className={isStudio ? undefined : 'w-3 h-3'} /> Copied</> : <><Copy className={isStudio ? undefined : 'w-3 h-3'} /> Copy</>}
                          </button>
                        </div>
                        <dl className={isStudio ? undefined : 'grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs'}>
                          <dt className="text-slate-400">Path</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300 break-all">{e.path}{e.query ? `?${e.query}` : ''}</dd>
                          <dt className="text-slate-400">Method</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">{e.method}</dd>
                          <dt className="text-slate-400">Status</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">{e.status}</dd>
                          <dt className="text-slate-400">Time</dt>
                          <dd className="font-mono text-slate-700 dark:text-slate-300">{fmtTime(e.ts)}</dd>
                          {e.client && (<><dt className="text-slate-400">Client</dt>
                            <dd className="font-mono text-slate-700 dark:text-slate-300">{e.client}</dd></>)}
                          {e.detail && (<><dt className="text-slate-400">Detail</dt>
                            <dd className="font-mono text-slate-700 dark:text-slate-300 break-all">{e.detail}</dd></>)}
                        </dl>
                        {e.traceback ? (
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Traceback</div>
                            <pre className="text-[11px] leading-relaxed font-mono text-slate-700 dark:text-slate-300 bg-white dark:bg-black/30 rounded-lg p-2 overflow-auto max-h-60 whitespace-pre-wrap break-all">{e.traceback}</pre>
                          </div>
                        ) : (
                          <p className="text-[11px] text-slate-400">No traceback (handled 5xx response — the route returned an error status without raising).</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* What clients saw — outage evidence uploaded from browsers */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-client-errors-card">
          <div className={isStudio ? 'st31-ch' : 'flex items-center justify-between mb-1'}>
            {isStudio
              ? <h2 className="st31-ct">What clients saw</h2>
              : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                  <MonitorSmartphone className="w-4 h-4 text-rose-500" /> What Clients Saw
                </div>}
            {clientErrs.length > 0 && (
              <button onClick={() => copy(clientErrs.map(clientErrToText).join('\n'), 'all-client')}
                data-testid="client-errors-copy-all"
                className={isStudio ? 'st-btn st-ghost st-sm st31-cr' : 'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg border border-slate-200 dark:border-white/15 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'}>
                {copiedId === 'all-client' ? <><Check className={isStudio ? undefined : 'w-3 h-3'} /> Copied</> : <><Copy className={isStudio ? undefined : 'w-3 h-3'} /> Copy all</>}
              </button>
            )}
          </div>
          <div className={isStudio ? 'st31-cb' : undefined}>
          <p className={isStudio ? 'st31-cap' : 'text-[11px] text-slate-500 dark:text-slate-400 mb-3'}>
            Failures recorded IN users' browsers (buffered locally during outages, uploaded on recovery) — the exact 502/520/network evidence the platform asks for. Survives full blackouts and redeploys.
          </p>
          {clientSummary.length > 0 && (
            <div className={isStudio ? 'st-chips' : 'flex flex-wrap gap-2 mb-3'} data-testid="client-errors-summary">
              {clientSummary.map((s, i) => (
                <span key={i} data-testid={`client-summary-chip-${i}`}
                  className={isStudio
                    ? `st-chip st-tb-xs ${s.status ? 'st-tb-bad' : 'st-tb-warn'}`
                    : `inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg ${s.status ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300' : 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300'}`}>
                  {s.status ? s.status : isStudio ? 'Network' : 'NETWORK'} × {s.count}
                  {!isStudio && <span className="font-normal opacity-70">(24h)</span>}
                </span>
              ))}
              {isStudio && <span className="st31-cap">last 24 hours</span>}
            </div>
          )}
          {clientErrs.length === 0 ? (
            isStudio
              ? <div className="st31-quiet" data-testid="client-errors-empty"><b>No client-side failure reports 🎉</b><span>Reports appear here after a viewer&rsquo;s app reconnects following an outage.</span></div>
              : <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="client-errors-empty">No client-side failure reports. 🎉 (Reports appear here after users' apps reconnect following an outage.)</p>
          ) : (
            <div className={isStudio ? 'st31-scroll' : 'max-h-96 overflow-auto divide-y divide-slate-100 dark:divide-white/5'}>
              {clientErrs.map((e, i) => (
                <div key={`${e.ts}-${e.session}-${i}`} className={isStudio ? 'st31-tl' : 'py-2 flex items-start gap-3 text-sm'} data-testid={`client-error-row-${i}`}>
                  <span className={isStudio
                    ? `st-chip st-tb-xs st31-mono ${e.status ? 'st-tb-bad' : 'st-tb-warn'}`
                    : `font-mono text-xs px-1.5 py-0.5 rounded shrink-0 ${e.status ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300' : 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300'}`}>
                    {clientStatusLabel(e)}
                  </span>
                  <div className={isStudio ? 'st31-b' : 'min-w-0 flex-1'}>
                    <div className={isStudio ? 'st31-mono st31-x' : 'font-mono text-xs text-slate-700 dark:text-slate-300 truncate'}>{e.method} {e.path}</div>
                    <div className={isStudio ? 'st31-m' : 'text-[11px] text-slate-400'}>
                      {fmtTime(e.ts)}{e.message ? ` · ${e.message}` : ''} · {e.app}{e.online === false ? ' · device offline' : ''}
                    </div>
                  </div>
                  <button onClick={() => copy(clientErrToText(e), `ce-${i}`)} data-testid={`client-error-copy-${i}`}
                    className={isStudio ? 'st-ibtn st31-copy' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shrink-0'}
                    style={isStudio ? { width: 28, height: 28 } : undefined} aria-label="Copy this report">
                    {copiedId === `ce-${i}` ? <Check className={isStudio ? undefined : 'w-3.5 h-3.5'} /> : <Copy className={isStudio ? undefined : 'w-3.5 h-3.5'} />}
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Scheduler */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-scheduler-card">
          {isStudio
            ? <div className="st31-ch"><h2 className="st31-ct">Background jobs<small>{snap?.scheduler?.job_count ?? 0} jobs · {snap?.scheduler?.running ? 'running' : 'stopped'}</small></h2></div>
            : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white mb-3">
                <ListChecks className="w-4 h-4 text-indigo-500" /> Background Jobs
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                  ({snap?.scheduler?.job_count ?? 0} jobs · {snap?.scheduler?.running ? 'running' : 'stopped'})
                </span>
              </div>}
          <div className={isStudio ? 'st31-cb' : undefined}>
          {isStudio && (snap?.scheduler?.jobs || []).length === 0 && (
            <div className="st31-quiet"><b>No background jobs registered</b></div>
          )}
          <div className={isStudio ? 'st31-scroll-sm' : 'max-h-72 overflow-auto divide-y divide-slate-100 dark:divide-white/5'}>
            {(snap?.scheduler?.jobs || []).map((j) => (
              <div key={j.id} className={isStudio ? 'st31-erow' : 'py-2 flex items-center justify-between text-sm gap-3'}>
                <span className={isStudio ? 'st31-mono st31-x' : 'font-mono text-xs text-slate-700 dark:text-slate-300 truncate'}>{j.id}</span>
                <span className={isStudio ? 'st31-m' : 'text-[11px] text-slate-400 shrink-0'}>next {fmtTime(j.next_run)}</span>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* Daily trend */}
        {/* Worship AI usage & health */}
        <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5 mb-6'} data-testid="health-ai-card">
          {isStudio
            ? <div className="st31-ch"><h2 className="st31-ct">Worship AI</h2></div>
            : <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white mb-4">
                <Bot className="w-4 h-4 text-purple-500" /> Worship AI
              </div>}
          <div className={isStudio ? 'st31-cb' : undefined}>
            <AIInsights adminQS={`user_email=${encodeURIComponent(email || '')}`} />
          </div>
        </div>

        {daily.length > 0 && (
          <div className={isStudio ? 'st31-card' : 'rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5'} data-testid="health-daily-card">
            {isStudio
              ? <div className="st31-ch"><h2 className="st31-ct">Daily error trend<small>last 30 days</small></h2></div>
              : <div className="font-semibold text-slate-900 dark:text-white mb-3">Daily Error Trend (30d)</div>}
            <div className={isStudio ? 'st31-cb' : undefined}>
            <div className={isStudio ? 'st31-dbars' : 'flex items-end gap-1 h-24'}>
              {[...daily].reverse().map((d) => {
                const max = Math.max(1, ...daily.map(x => x.errors_24h || 0));
                const h = Math.max(4, Math.round((d.errors_24h || 0) / max * 96));
                return (
                  <div key={d.date} title={`${d.date}: ${d.errors_24h} errors`}
                    className={isStudio ? (d.errors_24h > 0 ? 'st31-warnb' : undefined) : `flex-1 rounded-t ${d.errors_24h > 0 ? 'bg-amber-400' : 'bg-emerald-300'}`}
                    style={{ height: `${h}px` }} />
                );
              })}
            </div>
            {isStudio && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--st-fg3)' }}>
                <span>{daily.length ? [...daily].reverse()[0]?.date : ''}</span><span>today</span>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
